export default class OnboardingVideoModal extends HTMLElement {
  connectedCallback () {
    this.modalType = this.getAttribute('data-modal-type') || 'generic'
    this.configKey = this.getAttribute('data-config-key') || `dismissed_${this.modalType}_video_modal`
    this.storageKey = this.getAttribute('data-storage-key') || `doccu_dismissed_${this.modalType}_video`
    this.autoOpen = this.getAttribute('data-auto-open') === 'true'

    this.overlay = this.querySelector('[data-video-modal-target="overlay"]')
    this.card = this.querySelector('[data-video-modal-target="card"]')
    this.video = this.querySelector('video')
    this.playOverlay = this.querySelector('[data-video-modal-target="playOverlay"]')
    this.closeBtn = this.querySelector('[data-video-modal-target="closeBtn"]')
    this.dismissBtn = this.querySelector('[data-video-modal-target="dismissBtn"]')
    this.primaryCta = this.querySelector('[data-video-modal-target="primaryCta"]')

    // Bind event listeners
    this.closeBtn?.addEventListener('click', this.onDismissClick)
    this.dismissBtn?.addEventListener('click', this.onDismissClick)
    this.overlay?.addEventListener('click', this.onOverlayClick)
    this.playOverlay?.addEventListener('click', this.onPlayClick)
    this.primaryCta?.addEventListener('click', this.onPrimaryCtaClick)

    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('doccu:open-video-modal', this.onCustomOpenEvent)

    // Check auto-open condition: autoOpen must be true AND not dismissed in localStorage
    if (this.autoOpen && !this.isDismissedInStorage()) {
      // Small delay to allow page render/animations to settle
      setTimeout(() => {
        this.open()
      }, 300)
    }
  }

  disconnectedCallback () {
    this.closeBtn?.removeEventListener('click', this.onDismissClick)
    this.dismissBtn?.removeEventListener('click', this.onDismissClick)
    this.overlay?.removeEventListener('click', this.onOverlayClick)
    this.playOverlay?.removeEventListener('click', this.onPlayClick)
    this.primaryCta?.removeEventListener('click', this.onPrimaryCtaClick)

    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('doccu:open-video-modal', this.onCustomOpenEvent)
  }

  isDismissedInStorage () {
    try {
      return localStorage.getItem(this.storageKey) === 'true'
    } catch {
      return false
    }
  }

  onCustomOpenEvent = (e) => {
    if (!e.detail?.type || e.detail?.type === this.modalType) {
      this.open()
    }
  }

  onOverlayClick = (e) => {
    if (e.target === this.overlay) {
      this.dismiss()
    }
  }

  onKeyDown = (e) => {
    if (e.key === 'Escape' && !this.classList.contains('hidden')) {
      this.dismiss()
    }
  }

  onDismissClick = (e) => {
    e?.preventDefault()
    this.dismiss()
  }

  onPrimaryCtaClick = () => {
    // Save dismissal when primary CTA is clicked so it doesn't pop up again
    this.persistDismissal()
    this.close()
  }

  onPlayClick = () => {
    if (!this.video) return

    if (this.video.paused) {
      this.video.play()
      if (this.playOverlay) {
        this.playOverlay.classList.add('opacity-0', 'pointer-events-none')
      }
    } else {
      this.video.pause()
      if (this.playOverlay) {
        this.playOverlay.classList.remove('opacity-0', 'pointer-events-none')
      }
    }
  }

  open () {
    this.classList.remove('hidden')
    this.setAttribute('aria-hidden', 'false')
    document.body.classList.add('overflow-hidden')

    // Reset video play overlay state
    if (this.playOverlay && this.video?.paused) {
      this.playOverlay.classList.remove('opacity-0', 'pointer-events-none')
    }

    // Animate in
    requestAnimationFrame(() => {
      if (this.overlay) {
        this.overlay.classList.remove('opacity-0')
        this.overlay.classList.add('opacity-100')
      }
      if (this.card) {
        this.card.classList.remove('opacity-0', 'scale-95')
        this.card.classList.add('opacity-100', 'scale-100')
      }
    })
  }

  close () {
    if (this.video && !this.video.paused) {
      this.video.pause()
    }

    if (this.card) {
      this.card.classList.remove('opacity-100', 'scale-100')
      this.card.classList.add('opacity-0', 'scale-95')
    }
    if (this.overlay) {
      this.overlay.classList.remove('opacity-100')
      this.overlay.classList.add('opacity-0')
    }

    setTimeout(() => {
      this.classList.add('hidden')
      this.setAttribute('aria-hidden', 'true')
      document.body.classList.remove('overflow-hidden')
    }, 200)
  }

  async dismiss () {
    await this.persistDismissal()
    this.close()
  }

  async persistDismissal () {
    // 1. Client-side localStorage persistence
    try {
      localStorage.setItem(this.storageKey, 'true')
    } catch (e) {
      console.warn('Unable to persist video dismissal to localStorage:', e)
    }

    // 2. Server-side /user_configs persistence
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''
    try {
      await fetch('/user_configs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          user_config: {
            key: this.configKey,
            value: true
          }
        })
      })
    } catch (err) {
      console.warn(`Failed to save video modal dismissal (${this.configKey}):`, err)
    }

    // 3. Emit custom event
    window.dispatchEvent(new CustomEvent('doccu:video-modal-dismissed', {
      detail: { modalType: this.modalType }
    }))
  }
}
